import LocalImages from "assets/images/local-images";
import Content, { ContentNS } from "content/content";
import Card from "features/checklist/components/card/Card";
import ResolvedCard from "features/checklist/components/resolved-card/ResolvedCard";
import SuccessBanner from "features/shared/components/banners/success-banner/SuccessBanner";
import useNavigateBackInfo, {
  PageType,
} from "features/shared/hooks/navigation/useNavigateBackInfo";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import usePaxLevelAnalytics from "features/shared/hooks/analytics/usePaxLevelAnalytics";
import usePaxSession from "features/shared/hooks/usePaxSession";
import PageHeader from "features/shared/styles/page-header/PageHeader.styled";
import Body from "features/shared/styles/typography/Body.styled";
import { ButtonTitle } from "features/shared/styles/typography/ButtonTitle.styled";
import { TertiaryHeader } from "features/shared/styles/typography/TertiaryHeader.styled";
import ChecklistItemStatus from "models/checklist/checklist-item/checklist-item-status";
import { ChecklistItemType } from "models/checklist/checklist-item/checklist-item-type";
import ChecklistResolutionStatus from "models/checklist/checklist-resolution-status";
import { DocProcessingStatus } from "models/doc-processing-status";
import { FC, useEffect } from "react";
import { useAppStore } from "store/store";

import useFlowRedirect from "../shared/hooks/navigation/useFlowRedirect";
import PaxError from "./components/pax-error/PaxError";
import CardsContainer from "./styles/CardsContainer.styled";
// @ts-expect-error: This is how we utilize dynamic imports with WebPack NormalModuleReplacementPlugin (webpack.config.js)
import EditButton from "./styles/EditButton-APP_CLIENT.styled";
import StyledChecklistSection from "./styles/StyledChecklistSection.styled";
import Title from "./styles/Title.styled";
import { Summary } from "features/shared/styles/typography/Summary.styled";
import { PaxCheckRuleType } from "api";

const Checklist: FC = () => {
  useAppStore((state) => state.locale);

  const [shouldRedirect, redirect] = useFlowRedirect();

  const navigate = useNavigateWithQuery();
  const direction = Content.shared.instance.dir();

  const paxSession = usePaxSession();

  const setProcessingStatus = useAppStore((state) => state.setProcessingStatus);
  const setProcessingMayTakeLong = useAppStore(
    (state) => state.setProcessingMayTakeLong
  );

  const textChecklist = (s: string, args: any = {}) =>
    Content.shared.t(s, { ns: ContentNS.checklist, ...args });

  const textNationality = (s: string, args: any = {}) =>
    Content.shared.t(s, { ns: ContentNS.nationalities, ...args });

  const backLinkInfo = useNavigateBackInfo(PageType.Checklist);

  const analyticsSendPageView = usePaxLevelAnalytics();

  useEffect(() => {
    // reset processing status when navigating to checklist
    setProcessingStatus(DocProcessingStatus.None, null);
    setProcessingMayTakeLong(null);
    // redirect to other page if needed
    if (shouldRedirect === true) {
      redirect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirect]);

  useEffect(() => {
    if (shouldRedirect === false) {
      analyticsSendPageView();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allItems = paxSession?.checklist.items;

  const entryItems =
    allItems?.filter(
      (item) => item.ruleOrigin.type === PaxCheckRuleType.ENTRY
    ) || [];

  const transitItems =
    allItems?.filter(
      (item) => item.ruleOrigin.type === PaxCheckRuleType.TRANSIT
    ) || [];

  const exitItems =
    allItems?.filter(
      (item) => item.ruleOrigin.type === PaxCheckRuleType.EXIT
    ) || [];

  const isEntryNotAllowed =
    paxSession?.checklist.resolutionStatus ===
    ChecklistResolutionStatus.EntryNotAllowed;

  const isNotEligiblePassenger =
    paxSession?.checklist.resolutionStatus ===
    ChecklistResolutionStatus.NotEligible;

  const onEditClick = () => {
    if (!backLinkInfo) {
      // there is no back link - use default
      navigate("../questions", {
        state: { isEditing: true },
      });
      return;
    }

    // there is back link
    navigate(backLinkInfo.to, {
      state: backLinkInfo.state,
    });
  };

  if (shouldRedirect) {
    return null;
  }

  return (
    <StyledChecklistSection>
      {paxSession?.checklist.resolutionStatus ===
      ChecklistResolutionStatus.ReadyToFly ? (
        <SuccessBanner />
      ) : null}
      <PageHeader>
        <Title direction={direction}>
          <TertiaryHeader>{`${textChecklist(
            "checklist-title"
          )}`}</TertiaryHeader>
          {backLinkInfo ? (
            <EditButton onClick={onEditClick}>
              <img src={LocalImages.shared.images!.icons.edit} alt="edit" />
              <ButtonTitle>{backLinkInfo.title}</ButtonTitle>
            </EditButton>
          ) : null}
        </Title>
        <Body>{`${textChecklist("checklist-subtitle")}`}</Body>
      </PageHeader>

      {isEntryNotAllowed ? (
        <PaxError
          errorDescription={`${textChecklist("pax-entry-not-allowed")}`}
          hasEditSettingsButton
          hasReturnToBookingButton
        />
      ) : null}

      {isNotEligiblePassenger ? (
        <PaxError
          errorDescription={`${textChecklist("pax-not-eligible")}`}
          hasEditSettingsButton
          hasReturnToBookingButton
        />
      ) : null}

      {entryItems.length > 0 ? (
        <>
          <Summary>{`${textChecklist("category.entry", {
            country: textNationality(
              entryItems[0].ruleOrigin.country.toUpperCase()
            ),
          })}`}</Summary>
          <CardsContainer>
            {entryItems.map((item) => {
              if (item.status === ChecklistItemStatus.Resolved) {
                return <ResolvedCard key={item.id} item={item} />;
              }

              if (item.itemType === ChecklistItemType.TravelDoc) {
                return <Card key={item.id} item={item} highlight />;
              }

              return <Card key={item.id} item={item} />;
            })}
          </CardsContainer>
        </>
      ) : null}

      {transitItems.length > 0 ? (
        <>
          <Summary>{`${textChecklist("category.transit", {
            country: textNationality(
              transitItems[0].ruleOrigin.country.toUpperCase()
            ),
          })}`}</Summary>
          <CardsContainer>
            {transitItems.map((item) => {
              if (item.status === ChecklistItemStatus.Resolved) {
                return <ResolvedCard key={item.id} item={item} />;
              }

              if (item.itemType === ChecklistItemType.TravelDoc) {
                return <Card key={item.id} item={item} highlight />;
              }

              return <Card key={item.id} item={item} />;
            })}
          </CardsContainer>
        </>
      ) : null}

      {exitItems.length > 0 ? (
        <>
          <Summary>{`${textChecklist("category.exit", {
            country: textNationality(
              exitItems[0].ruleOrigin.country.toUpperCase()
            ),
          })}`}</Summary>
          <CardsContainer>
            {exitItems.map((item) => {
              if (item.status === ChecklistItemStatus.Resolved) {
                return <ResolvedCard key={item.id} item={item} />;
              }

              if (item.itemType === ChecklistItemType.TravelDoc) {
                return <Card key={item.id} item={item} highlight />;
              }

              return <Card key={item.id} item={item} />;
            })}
          </CardsContainer>
        </>
      ) : null}
    </StyledChecklistSection>
  );
};

export default Checklist;
